import { BigintIsh, Currency, Token } from '@kodiak-finance/sdk-core'
import { computePoolAddress, FeeAmount, Pool } from '@kodiak-finance/v3-sdk'
import V3_POOL_ABI from 'abis/v3-pool.json'
import { useConnection } from 'hooks/useConnection'
import JSBI from 'jsbi'
import { useMemo } from 'react'
import type { Abi, Address } from 'viem'

import { V3_FACTORY_ADDRESSES } from '../constants/addresses'
import { useReadContractMultiple } from './useReadContractMultiple'

// Classes are expensive to instantiate, so this caches the recently instantiated pools.
// This avoids re-instantiating pools as the other pools in the same request are loaded.
class PoolCache {
  // Evict after 128 entries. Empirically, a swap uses 64 entries.
  private static MAX_ENTRIES = 128

  // These are FIFOs, using unshift/pop. This makes recent entries faster to find.
  private static pools: Pool[] = []
  private static addresses: { key: string; address: string }[] = []

  static getPoolAddress(factoryAddress: string, tokenA: Token, tokenB: Token, fee: FeeAmount): string {
    if (this.addresses.length > this.MAX_ENTRIES) {
      this.addresses = this.addresses.slice(0, this.MAX_ENTRIES / 2)
    }

    const { address: addressA } = tokenA
    const { address: addressB } = tokenB
    const key = `${factoryAddress}:${addressA}:${addressB}:${fee.toString()}`
    const found = this.addresses.find((address) => address.key === key)
    if (found) return found.address

    // For some reason, this only works in this hardcoded flipped structure
    const address = {
      key,
      address: computePoolAddress({
        // factoryAddress: '0x802b2f0c1f647d54b2eaa212df75bf069eb218ab',
        // factoryAddress: '0x802b2f0c1f647d54b2eaa212df75bf069eb218ab',
        factoryAddress,
        tokenA: tokenB,
        tokenB: tokenA,
        fee,
        // initCodeHashManualOverride: '0x701582da8b11337ef1e7537e21bffb5a7f8241261b4e459cae6b306cda28e619',
      }),
    }
    this.addresses.unshift(address)
    return address.address
  }

  static getPool(
    tokenA: Token,
    tokenB: Token,
    fee: FeeAmount,
    sqrtPriceX96: BigintIsh,
    liquidity: BigintIsh,
    tick: number
  ): Pool {
    if (this.pools.length > this.MAX_ENTRIES) {
      this.pools = this.pools.slice(0, this.MAX_ENTRIES / 2)
    }

    const found = this.pools.find(
      (pool) =>
        pool.token0 === tokenA &&
        pool.token1 === tokenB &&
        pool.fee === fee &&
        JSBI.EQ(pool.sqrtRatioX96, sqrtPriceX96) &&
        JSBI.EQ(pool.liquidity, liquidity) &&
        pool.tickCurrent === tick
    )
    if (found) return found

    const pool = new Pool(tokenA, tokenB, fee, sqrtPriceX96, liquidity, tick)
    this.pools.unshift(pool)
    return pool
  }
}

export enum PoolState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
}

export function usePools(
  poolKeys: [Currency | undefined, Currency | undefined, FeeAmount | undefined][]
): [PoolState, Pool | null][] {
  const { chainId } = useConnection()

  const poolTokens: ([Token, Token, FeeAmount] | undefined)[] = useMemo(() => {
    if (!chainId) return new Array(poolKeys.length)

    return poolKeys.map(([currencyA, currencyB, feeAmount]) => {
      if (currencyA && currencyB && feeAmount) {
        const tokenA = currencyA.wrapped
        const tokenB = currencyB.wrapped
        if (tokenA.equals(tokenB)) return undefined
        return tokenA.sortsBefore(tokenB) ? [tokenA, tokenB, feeAmount] : [tokenB, tokenA, feeAmount]
      }
      return undefined
    })
  }, [chainId, poolKeys])

  const poolAddresses: (string | undefined)[] = useMemo(() => {
    const v3CoreFactoryAddress = chainId && V3_FACTORY_ADDRESSES[chainId]
    if (!v3CoreFactoryAddress) return new Array(poolTokens.length)

    return poolTokens.map((value) => value && PoolCache.getPoolAddress(v3CoreFactoryAddress, ...value))
  }, [chainId, poolTokens])

  // Create contracts array for batch reading slot0 and liquidity for all pools
  const slot0Contracts = useMemo(() => {
    return poolAddresses
      .filter((address) => address !== undefined)
      .map((address) =>
        address
          ? {
              address: address as Address,
              abi: V3_POOL_ABI as Abi,
              functionName: 'slot0' as const,
            }
          : null
      )
  }, [poolAddresses])
  const liquidityContracts = useMemo(() => {
    return poolAddresses
      .filter((address) => address !== undefined)
      .map((address) =>
        address
          ? {
              address: address as Address,
              abi: V3_POOL_ABI as Abi,
              functionName: 'liquidity' as const,
            }
          : null
      )
  }, [poolAddresses])

  const { data: slot0Results, isLoading: isLoadingSlot0 } = useReadContractMultiple({
    contracts: slot0Contracts as any,
    query: {
      enabled: slot0Contracts.length > 0,
    },
  })
  const { data: liquidityResults, isLoading: isLoadingLiquidity } = useReadContractMultiple({
    contracts: liquidityContracts as any,
    query: {
      enabled: liquidityContracts.length > 0,
    },
  })

  return useMemo(() => {
    return poolKeys.map((_key, index) => {
      const tokens = poolTokens[index]
      if (!tokens) return [PoolState.INVALID, null]
      const [token0, token1, fee] = tokens

      // Each pool has 2 results: slot0 and liquidity
      const slot0Result = slot0Results?.[index]
      const liquidityResult = liquidityResults?.[index]

      // Check if still loading
      if (isLoadingSlot0 || isLoadingLiquidity) return [PoolState.LOADING, null]

      // Check if pool address exists
      if (!poolAddresses[index]) return [PoolState.INVALID, null]

      // Check if results exist and are successful
      if (!slot0Result || !liquidityResult) return [PoolState.LOADING, null]
      if (slot0Result.status === 'failure' || liquidityResult.status === 'failure') return [PoolState.NOT_EXISTS, null]

      // Extract the data
      const slot0 = slot0Result.result as readonly [bigint, number, number, number, number, number, boolean]
      const liquidity = liquidityResult.result as bigint

      if (!slot0 || (!liquidity && liquidity !== 0n)) return [PoolState.NOT_EXISTS, null]

      const sqrtPriceX96 = slot0[0]
      const tick = slot0[1]

      if (!sqrtPriceX96 || sqrtPriceX96 === 0n) return [PoolState.NOT_EXISTS, null]

      try {
        // Convert bigint to string for SDK compatibility
        const pool = PoolCache.getPool(token0, token1, fee, sqrtPriceX96.toString(), liquidity.toString(), tick)
        return [PoolState.EXISTS, pool]
      } catch (error) {
        console.error('Error when constructing the pool', error)
        return [PoolState.NOT_EXISTS, null]
      }
    })
  }, [slot0Results, liquidityResults, isLoadingSlot0, isLoadingLiquidity, poolKeys, poolTokens, poolAddresses])
}

export function usePool(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  feeAmount: FeeAmount | undefined
): [PoolState, Pool | null] {
  const poolKeys: [Currency | undefined, Currency | undefined, FeeAmount | undefined][] = useMemo(
    () => [[currencyA, currencyB, feeAmount]],
    [currencyA, currencyB, feeAmount]
  )

  return usePools(poolKeys)[0]
}
